// The list column (HomeMail): the line of the selection with the bulk actions (and of the search made in the global one, R-U23), and the letters — sender and time,
// subject with marks, the start of the text; unread in bold; Shift and Ctrl/⌘ select several.

import { useEffect, useRef } from 'react';
import type { Id } from '@confdeck/core-shared';
import { useMeta } from '../../sync/react.tsx';
import { Icon } from '../../ui/icons.tsx';
import { IconButton, Checkbox, Count, cx, titleIfCut } from '../../ui/primitives.tsx';
import { ListBar, ListBody, ListEmpty, ListHead, ListRow } from '../../ui/ListReader.tsx';
import { FOLDER_LIST, listTime, senderOf, type Letter, type View } from './model.ts';
import type { Mailbox } from './use-letters.ts';
import s from './mail.module.css';

const plural = (n: number, forms: [string, string, string]) => forms[n % 10 === 1 && n % 100 !== 11 ? 0 : n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 10 || n % 100 >= 20) ? 1 : 2];
const FOLDER_TAG: Record<string, string> = { sent: 'Отправлено', drafts: 'Черновик', archive: 'Архив' };

export function LetterList({ list, view, query, onQuery, openId, checked, onChecked, onOpen, onMove, onMark, boxes, box }: {
  /** `query` — the search chosen in the global one («Все письма по …»); `onQuery('')` drops it. */
  list: Letter[]; view: View; query: string; onQuery(q: string): void;
  openId: Id | null; checked: Set<Id>; onChecked(c: Set<Id>): void; onOpen(l: Letter): void;
  onMove(ids: Id[], folder: 'inbox' | 'archive' | 'trash' | 'junk'): void; onMark(ids: Id[], c: { unread?: boolean; starred?: boolean }): void;
  boxes: Mailbox[]; box: Id | 'all';
}) {
  const meta = useMeta();
  const failed = (l: Letter) => !!l.status && (meta.term(l.status)?.key ?? '').endsWith('failed');
  const anchor = useRef<number | null>(null);
  const scroll = useRef<HTMLDivElement | null>(null);
  const title = query ? 'Поиск' : FOLDER_LIST.find((f) => f.key === view)?.label ?? 'Почта';
  const unread = list.filter((l) => l.unread).length;
  const ids = [...checked];
  const all = checked.size > 0 && checked.size === list.length;
  const binned = view === 'trash' || view === 'junk';
  const mailbox = box === 'all' ? null : boxes.find((b) => b.id === box);

  // The open letter stays in sight when the keys walk the list.
  useEffect(() => { scroll.current?.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: 'nearest' }); }, [openId]);

  const click = (l: Letter, i: number, e: React.MouseEvent) => {
    if (e.shiftKey && anchor.current !== null) {
      const [a, b] = [Math.min(anchor.current, i), Math.max(anchor.current, i)];
      onChecked(new Set([...checked, ...list.slice(a, b + 1).map((x) => x.id)]));
      return;
    }
    anchor.current = i;
    if (e.metaKey || e.ctrlKey) { toggle(l.id); return; }
    onOpen(l);
  };
  const toggle = (id: Id) => { const n = new Set(checked); if (n.has(id)) n.delete(id); else n.add(id); onChecked(n); };
  const allRead = list.filter((l) => checked.has(l.id)).every((l) => !l.unread);

  return (
    <>
      <ListHead title={title} />

      <ListBar>
        <Checkbox label={all ? 'Снять выбор' : 'Выбрать все'} checked={all ? true : checked.size > 0 ? 'mixed' : false}
          onChange={() => onChecked(all || checked.size ? new Set() : new Set(list.map((l) => l.id)))} />
        {checked.size > 0 ? (
          <>
            <span><Count value={checked.size} /> выбрано</span>
            <span className={s.barActions}>
              <IconButton size="sm" icon={allRead ? 'mailUnread' : 'mailRead'} label="Прочитано или нет" hint={allRead ? 'Отметить непрочитанными (U)' : 'Отметить прочитанными (U)'} onClick={() => onMark(ids, { unread: allRead })} />
              <IconButton size="sm" icon="star" label="Важное" hint="Важное (S)" onClick={() => onMark(ids, { starred: true })} />
              {binned
                ? <IconButton size="sm" icon="undo" label="Вернуть во входящие" hint="Вернуть во «Входящие»" onClick={() => onMove(ids, 'inbox')} />
                : <>
                  <IconButton size="sm" icon="archive" label="В архив" hint="В архив (E)" onClick={() => onMove(ids, 'archive')} />
                  <IconButton size="sm" icon="junk" label="В спам" hint="В спам (!)" onClick={() => onMove(ids, 'junk')} />
                </>}
              {view !== 'trash' && <IconButton size="sm" icon="trash" label="В корзину" hint="В корзину (#)" onClick={() => onMove(ids, 'trash')} />}
            </span>
          </>
        ) : (
          <span>
            {query ? `Найдено ${list.length} по «${query}»` : `${list.length} ${plural(list.length, ['письмо', 'письма', 'писем'])}`}
            {unread > 0 && <span className={s.unreadCount}> · {unread} {plural(unread, ['новое', 'новых', 'новых'])}</span>}
          </span>
        )}
        {query && !checked.size && <span className={s.barActions}><IconButton size="sm" icon="close" label="Сбросить поиск" hint="Сбросить поиск" onClick={() => onQuery('')} /></span>}
      </ListBar>

      {mailbox?.syncState === 'error' && <div className={s.notice} role="status"><Icon name="junk" size={14} />Ящик не синхронизируется: {mailbox.syncError}</div>}

      {list.length === 0 ? (
        <ListEmpty icon={query ? 'search' : 'inbox'} text={query ? 'Ничего не нашлось' : 'Здесь пусто'} hint={query ? 'Попробуйте from:имя, to:адрес, has:вложение, is:непрочитанные' : undefined} />
      ) : (
        <ListBody label={title} multi bodyRef={scroll}>
          {list.map((l, i) => (
            <ListRow key={l.id} selected={l.id === openId} strong={l.unread} data-unread={l.unread || undefined} data-checked={checked.has(l.id) || undefined}
              className={s.letterRow} onClick={(e) => click(l, i, e)}
              dot={l.unread ? 'new' : undefined} dotLabel={l.unread ? 'Не прочитано' : undefined}
              check={{ checked: checked.has(l.id), label: 'Выбрать письмо', onChange: () => { anchor.current = i; toggle(l.id); } }}
              title={senderOf(l)}
              aside={<>
                {query && FOLDER_TAG[l.folder] && <span className={s.tag}>{FOLDER_TAG[l.folder]}</span>}
                {failed(l) && <span className={s.tag} data-tone="danger">Не отправлено</span>}
                <time dateTime={l.date}>{listTime(l.date)}</time>
              </>}
              sub={<>
                <span className={s.subject} onMouseEnter={titleIfCut}>{l.subject || '(без темы)'}</span>
                {l.attachments.length > 0 && <span className={s.mark} aria-label="Есть вложения"><Icon name="paperclip" size={14} /></span>}
                <button type="button" className={s.star} data-on={l.starred || undefined} aria-pressed={l.starred} aria-label={l.starred ? 'Снять «Важное»' : 'Важное'}
                  onClick={(e) => { e.stopPropagation(); onMark([l.id], { starred: !l.starred }); }}>
                  <Icon name={l.starred ? 'starFilled' : 'star'} size={14} />
                </button>
              </>}
              sub2={l.snippet} />
          ))}
        </ListBody>
      )}
    </>
  );
}
